import { Component, EventEmitter, HostBinding, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AppliedTax, LineItemTaxes, RetailProvider, TaxRateService } from '@galaxy/billing';
import { AppKey, AppPartnerService, FieldMask } from '@galaxy/marketplace-apps';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { PackageService } from '@vendasta/marketplace-packages';
import { ConfigInterface, DurationInterface, Order, Status, UserInterface as User } from '@vendasta/sales-orders';
import {
  AppSdkService,
  convertLineItemsToBillingSummary,
  OrderDetailAgreement,
  PackageApiService,
  SalesOrderFormComponent,
  UILineItemWithEditionPricing,
} from '@vendasta/sales-ui';
import { OrderFormOptionsInterface, TaxOption } from '@vendasta/store';
import { BehaviorSubject, combineLatest, Observable, of, throwError, zip } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { PaymentElementFormComponent } from './components/payment-element-form/payment-element-form.component';
import { OrderTermsComponent } from './components/terms/order-terms.component';
import { ApproveEvent } from './orders.interface';
import { buildOrderItems } from './utils';
import { HttpErrorResponse } from '@angular/common/http';
import { ItemPricingTableConfig, ItemSelectorFilterType } from '@galaxy/inventory-ui';
import { RetailSummary } from './components/retail-summary/retail-summary.component';
import { getRetailSummaryFromOrder } from './shared/utils';

@Component({
  selector: 'orders-page',
  templateUrl: './order.component.html',
  styleUrls: ['./order.component.scss'],
  standalone: false,
})
export class OrderComponent implements OnInit {
  @HostBinding('class') class = 'orders';
  @Input() loading = true;
  @Input() error: boolean;

  private order$$: BehaviorSubject<Order> = new BehaviorSubject<Order>(null);
  protected order$: Observable<Order> = this.order$$.asObservable();

  @Input() set order(order: Order) {
    this.order$$.next(order);
  }

  @Input() orderDetailAgreements: OrderDetailAgreement[];
  @Input() orderFormOptions: OrderFormOptionsInterface;

  private orderConfig$$: BehaviorSubject<ConfigInterface> = new BehaviorSubject<ConfigInterface>(null);
  protected orderConfig$: Observable<ConfigInterface> = this.orderConfig$$.asObservable();

  @Input() set orderConfig(orderConfig: ConfigInterface) {
    this.orderConfig$$.next(orderConfig);
  }

  @Input() partnerName: string;
  @Input() fileUploadUrl: string;
  @Input() userOptions: User[];

  private submitDisabled$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  protected submitDisabled$: Observable<boolean> = this.submitDisabled$$.asObservable();

  @Input() set submitDisabled(submitDisabled: boolean) {
    this.submitDisabled$$.next(submitDisabled);
  }

  @Input() enableLanguageSelector = false; // true for public approval page, false for authn'd (atlas has a language selector)
  @Input() retailProvider: RetailProvider;
  @Input() stripeKey: string;

  @Input() title: string;
  @Input() subtitle: string;

  @Input() set expandDropdowns(expand: boolean) {
    if (expand) {
      this.orderDetailsCard.signalToOpenAllDropdowns();
    }
  }

  @Input() isInPreviewMode = false;
  @Input() isSMBPayableOverride = false;

  @Output() isDeclined: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() isApproved: EventEmitter<ApproveEvent> = new EventEmitter<ApproveEvent>();

  @ViewChild('orderDetailsCard') readonly orderDetailsCard;
  @ViewChild('salesOrderForm') salesOrderFormComponent: SalesOrderFormComponent;
  @ViewChild('terms') terms: OrderTermsComponent;
  @ViewChild(PaymentElementFormComponent) creditCardComponent: PaymentElementFormComponent;

  disableSubmitButton$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  disableSubmitButton$: Observable<boolean>;

  // pricing information
  orderItems$: Observable<UILineItemWithEditionPricing[]>; // the packages, products, or addons on the order
  taxOptions$: Observable<TaxOption[]>;
  contractDuration$: Observable<DurationInterface>;

  // terms
  termsFormGroup: UntypedFormGroup;
  orderFormGroup: UntypedFormGroup;

  signature = new FormControl('', [Validators.required, Validators.pattern('^.*\\S+.*$')]);
  signatureError$: Observable<string>;

  itemPricingTableConfig$: Observable<ItemPricingTableConfig>;
  retailSummary$: Observable<RetailSummary>;

  statusSubmittedForCustomerApproval = Status.SUBMITTED_FOR_CUSTOMER_APPROVAL;

  constructor(
    private translateService: TranslateService,
    private snackbarService: SnackbarService,
    @Inject(PackageService) private readonly packageSdk: PackageApiService,
    @Inject(AppPartnerService) private readonly appSdk: AppSdkService,
    private readonly taxRateService: TaxRateService,
    private formBuilder: UntypedFormBuilder,
  ) {
    this.termsFormGroup = this.formBuilder.group({});
    this.orderFormGroup = this.formBuilder.group({});
    this.orderFormGroup.addControl('signature', this.signature);
    this.signatureError$ = of(this.signature).pipe(
      switchMap((signature) => {
        return signature.hasError('required') || signature.hasError('pattern')
          ? this.translateService.get('LIB_ORDERS.COMMON.ORDERS.INVALID_USER_SIGNATURE_ERROR')
          : of('');
      }),
    );
  }

  ngOnInit(): void {
    const lineItems$ = this.order$.pipe(map((order) => order.lineItems));

    const packages$ = this.order$.pipe(
      switchMap((order) => {
        const packageLineItems = order.lineItems.filter((li) => !!li.packageId);
        if (packageLineItems.length > 0) {
          return this.packageSdk.getMulti(packageLineItems.map((li) => li.packageId));
        }
        return of([]);
      }),
    );

    const appsAndAddons$ = this.order$.pipe(
      switchMap((order) => {
        const appsAndAddonsLineItems = order.lineItems.filter((li) => !!li.appKey);
        const appsAndAddonsKeys = appsAndAddonsLineItems.map((app) => new AppKey(app.appKey));
        if (appsAndAddonsKeys.length > 0) {
          return this.appSdk.getMulti(
            appsAndAddonsKeys,
            order.partnerId,
            order.marketId,
            new FieldMask({ paths: ['billingId'] }),
            true,
          );
        }
        return of([]);
      }),
    );
    this.orderItems$ = zip(lineItems$, packages$, appsAndAddons$).pipe(
      map(([lineItems, packages, appsAndAddons]) => {
        return buildOrderItems(lineItems, packages, appsAndAddons, null);
      }),
    );

    this.taxOptions$ = combineLatest([this.order$, this.orderItems$]).pipe(
      switchMap(([order, orderItems]) => {
        if (order) {
          const shouldTaxApplyToAllSKUs = (orderItems || []).every(
            (li) => (li?.lineItem?.taxRateIds || []).length === 0,
          );
          const taxOptions = (order.taxOptions || []).map((opt) => {
            const applicableSKUs = shouldTaxApplyToAllSKUs
              ? orderItems.map((li) => li.billingId)
              : orderItems
                  .filter((li) => (li?.lineItem?.taxRateIds || []).includes(opt.taxRateId))
                  .map((li) => li.billingId);
            return {
              label: opt.label,
              percentageMultiplier: opt.percentageMultiplier,
              initialAmount: opt.initialAmount,
              taxRateId: opt.taxRateId,
              applicableSkus: applicableSKUs,
            };
          });
          return of(taxOptions);
        }
        return this.taxRateService
          .calculateLineItems(
            order.partnerId,
            order.businessId,
            Object.values(convertLineItemsToBillingSummary(orderItems)),
          )
          .pipe(
            catchError((err: HttpErrorResponse) => {
              console.error(err);
              return [];
            }),
            map((response) => {
              return response.appliedTaxes.map((rate: AppliedTax) => {
                const applicableSkus = this.buildApplicableSkus(rate, response.lineItemTaxes);

                return <TaxOption>{
                  label: rate.name,
                  percentageMultiplier: rate.percentage / 100,
                  initialAmount: rate.amount,
                  taxRateId: rate.taxRateId,
                  applicableSkus: applicableSkus,
                };
              });
            }),
          );
      }),
    );

    this.contractDuration$ = this.order$.pipe(
      map((order) => {
        return order.contractDuration;
      }),
    );

    this.disableSubmitButton$ = combineLatest([this.disableSubmitButton$$.asObservable(), this.submitDisabled$]).pipe(
      map(([disableSubmitButton, submitDisabled]) => {
        return disableSubmitButton || submitDisabled;
      }),
    );

    this.itemPricingTableConfig$ = this.order$.pipe(
      map((order) =>
        this.pricingTableConfig(order.partnerId, order.marketId, order.businessId, order.requestedActivation),
      ),
    );

    this.retailSummary$ = this.order$.pipe(
      map((salesOrder) => {
        return getRetailSummaryFromOrder(salesOrder);
      }),
    );
  }

  private pricingTableConfig(
    partnerId: string,
    marketId: string,
    accountGroupId: string,
    billingTermsDefaultStartDate: Date,
  ): ItemPricingTableConfig {
    const itemTypesAvailable: ItemSelectorFilterType[] = ['packages'];

    return {
      partnerId: partnerId,
      marketId: marketId,
      businessId: accountGroupId,
      pageSize: 15,
      hideOwnerName: true,
      hideRetail: false,
      hideQuantity: false,
      hideItemsWithOrderForms: false,
      itemTypesAvailable: itemTypesAvailable,
      enforceDependencies: true,
      quantityEditable: false,
      billingTermsConfig: {
        enabled: true,
        defaultStartDate: billingTermsDefaultStartDate,
      },
      hideColumns: ['wholesale_price'],
      hidePriceSummary: true,
      hideBorder: false,
      hidePackageContents: true,
      canAddItems: false,
      canEditInvoices: false,
      canScheduleActivations: false,
    };
  }

  buildApplicableSkus(rate: AppliedTax, lineItemTaxes: LineItemTaxes[]): string[] {
    return lineItemTaxes
      .filter((liTax) => liTax.taxRateIds.some((taxRateId) => taxRateId === rate.taxRateId))
      .map((liTax) => liTax.sku);
  }

  onApprove(): void {
    // TODO the Terms and Conditions checkboxes has a bug where only checking one is valid
    // either fix, or have a single checkbox to click for all terms
    if (!this.termsFormGroup.valid) {
      this.snackbarService.openErrorSnack('LIB_ORDERS.COMMON.ORDERS.TERMS_OF_SERVICE_ERROR');
      return;
    }
    if (!this.orderFormGroup.valid) {
      this.signature.markAsTouched();
      this.snackbarService.openErrorSnack('LIB_ORDERS.COMMON.ORDERS.ORDER_FORM_ERROR');
      return;
    }

    this.disableSubmitButton$$.next(true);

    const approveEvent: ApproveEvent = {
      customForm: this.salesOrderFormComponent.getCustomFormData(),
      commonForm: this.salesOrderFormComponent.getCommonFormData(),
      extraFields: this.salesOrderFormComponent.getExtraFieldsData(),
      terms: this.terms.getAgreements(),
    };

    const paymentMethod$ = (this.creditCardComponent?.getPaymentMethod() || of('')).pipe(
      catchError((err) => {
        this.snackbarService.openErrorSnack('LIB_ORDERS.COMMON.ORDERS.PAYMENT_METHOD_FAILURE');
        console.log(`Error getting payment method: ${err}`);
        return throwError(err);
      }),
    );

    combineLatest([paymentMethod$, this.order$])
      .pipe(take(1))
      .subscribe(([paymentMethod, order]) => {
        if (this.requiresPayment(order)) {
          if (paymentMethod) {
            this.isApproved.emit({
              ...approveEvent,
              paymentMethodToken: paymentMethod,
            });
          } else {
            this.disableSubmitButton$$.next(false);
            this.creditCardComponent.focusCardInput();
            this.snackbarService.openErrorSnack('LIB_ORDERS.COMMON.ORDERS.PAYMENT_METHOD_ERROR');
          }
        } else {
          this.isApproved.emit(approveEvent);
        }
      });
  }

  onDecline(): void {
    this.isDeclined.emit(true);
  }

  isSubmittedForCustomerApproval(orderStatus: Status): boolean {
    return orderStatus === Status.SUBMITTED_FOR_CUSTOMER_APPROVAL;
  }

  requiresPayment(order: Order): boolean {
    if (this.isSMBPayableOverride && this.isInPreviewMode && !!this.retailProvider) {
      return true;
    }
    if (this.isSubmittedForCustomerApproval(order?.status) && !!this.retailProvider) {
      return order?.orderIsSmbPayable;
    }
    return false;
  }

  refreshPage(): void {
    window.location.reload();
  }
}
